import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ManufacturingFacilityInput } from "__generated__/gql/graphql";
import Form, { OnValidSubmitHandler } from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import AddressFieldset, {
  addressFieldsetSchema,
  getHasAddressData,
  AddressFieldsetType,
} from "components/Shared/Forms/AddressFieldset";
import type { Writeable } from "utils/typescriptUtilis";
import {
  makePostgraphileErrorProcessor,
  getPostgraphileErrorMatchersForFields,
} from "utils/PostgraphileErrorExtensions";

export const manufacturingFacilityPrimitiveFields = ["name"] as const;

type PrimitiveInputs = Required<
  Pick<
    Writeable<ManufacturingFacilityInput>,
    typeof manufacturingFacilityPrimitiveFields[number]
  >
>;

export type ManageManufacturingFacilityFormFields = PrimitiveInputs & {
  address: AddressFieldsetType;
};

const schema: yup.ObjectSchema<ManageManufacturingFacilityFormFields> = yup
  .object()
  .shape({
    name: yup.string().trim().label("Name").required().max(255),
    address: addressFieldsetSchema
      .label("Address")
      .required()
      // eslint-disable-next-line no-template-curly-in-string
      .test("has-address", "${label} is required", (value) =>
        Boolean(value && getHasAddressData(value)),
      ),
  });

const { FormField } =
  createFormFieldComponents<ManageManufacturingFacilityFormFields>({
    schema,
  });

export const processApolloError =
  makePostgraphileErrorProcessor<PrimitiveInputs>(
    getPostgraphileErrorMatchersForFields(
      manufacturingFacilityPrimitiveFields,
      {
        type: "ManufacturingFacility",
      },
    ),
  );

export type FormProps = {
  defaultValues?: ManageManufacturingFacilityFormFields;
  formId: string;
  isLoading: boolean;
  onValidSubmit: OnValidSubmitHandler<ManageManufacturingFacilityFormFields>;
};

export default function ManufacturingFacilityForm({
  defaultValues,
  formId,
  isLoading,
  onValidSubmit,
}: FormProps) {
  const formContext = useForm<ManageManufacturingFacilityFormFields>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    formContext.reset(defaultValues);
  }, [defaultValues, formContext]);

  return (
    <Form
      formContext={formContext}
      id={formId}
      onSubmit={(evt) => {
        evt.stopPropagation();
      }}
      onValidSubmit={onValidSubmit}
      isLoaded={!isLoading}
    >
      <FormField name="name" />

      <AddressFieldset<ManageManufacturingFacilityFormFields>
        fieldNamePrefix="address"
        legendLabel="Address"
        my={8}
      />
    </Form>
  );
}
